/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { TUsers, useGetOpportunityFiltersList } from 'integration/resources/newOpportunities'
import { useBreakpointValue } from 'native-base'
import { useForm } from 'react-hook-form'
import { setUsersDisplayMap } from 'src/utils/usersDisplay'

import {
  OPPORTUNITY_FILTER_SCHEMAS,
  OPPORTUNITY_TYPES_OPTIONS,
  returnOPPORTUNITY_TYPE_ENUM
} from './constants'
import {
  UseOpportunityFilterScreen,
  TFieldNames,
  TFieldConfig,
  TFormFields
} from './OpportunityFilterScreen.types'

const toArray = (val: any): string[] =>
  val === undefined || val === null || val === ''
    ? []
    : String(val)
        .split(',')
        .map((v) => String(v).trim())
        .filter(Boolean)

export const useOpportunityFilterScreen: UseOpportunityFilterScreen = ({ navigation, route }) => {
  const isMobile = useBreakpointValue({ base: true, lg: false })

  const currentFilters = useMemo(() => {
    return {
      order_by: route?.params?.order_by,
      order_by_direction: route?.params?.order_by_direction,
      listType: route?.params?.listType
    }
  }, [route?.params])

  const currentSchema = OPPORTUNITY_FILTER_SCHEMAS[currentFilters?.listType]

  const opportunityType = returnOPPORTUNITY_TYPE_ENUM(currentFilters?.listType)

  const {
    data: filters,
    isLoading: isLoadingFilter,
    refetch: refetchFilter,
    isRefetching: isRefetchingFilter
  } = useGetOpportunityFiltersList(opportunityType, !!currentFilters?.listType && !!opportunityType)

  useEffect(() => {
    refetchFilter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityType])

  const closeRightSheet = useCallback(() => {
    navigation.goBack()
  }, [navigation])

  const { control, formState, reset, handleSubmit, watch } = useForm<TFormFields>({
    resolver: yupResolver(currentSchema),
    mode: 'onChange',
    defaultValues: {
      offer: toArray(route?.params?.offer),
      public_rating: toArray(route?.params?.public_rating),
      segment: toArray(route?.params?.segment),
      network: toArray(route?.params?.network),
      subsidiary: toArray(route?.params?.subsidiary),
      opportunity_type: toArray(route?.params?.opportunity_type)
        .map((id) => OPPORTUNITY_TYPES_OPTIONS.find((option) => option?.id.toString() === id)?.name)
        .filter((v): v is string => Boolean(v)),
      user_std_code: toArray(route?.params?.user_std_code)
    }
  })

  const handleSubmitForm = (values: TFormFields) => {
    const mapJoin = (arr?: string[]) => (arr?.length ? arr.join(',') : undefined)

    const mapOpportunityTypes = (names?: string[]) => {
      if (!names?.length) return undefined

      const ids = OPPORTUNITY_TYPES_OPTIONS.filter((opt) => names.includes(opt.name)).map((o) =>
        o.id.toString()
      )

      return ids.length ? ids.join(',') : undefined
    }

    const filterValues = {
      offer: mapJoin(values.offer),
      public_rating: mapJoin(values.public_rating),
      segment: mapJoin(values.segment),
      network: mapJoin(values.network),
      subsidiary: mapJoin(values.subsidiary),
      opportunity_type: mapOpportunityTypes(values.opportunity_type),
      user_std_code: mapJoin(values.user_std_code)
    }

    return isMobile
      ? navigation.navigate('Tab', {
          screen: 'Organization',
          params: {
            ...filterValues,
            ...currentFilters,
            // @TODO: Temporariamente desabilitado devido a pedido do cliente
            tabId: 2
          }
        })
      : navigation.navigate('OrganizationOpportunityList', {
          ...filterValues,
          ...currentFilters
        })
  }

  const handleResetForm = () => {
    const cleanFilters = {
      offer: undefined,
      public_rating: undefined,
      segment: undefined,
      network: undefined,
      subsidiary: undefined,
      opportunity_type: undefined,
      user_std_code: undefined
    }

    navigation.setParams({ ...cleanFilters })

    reset(
      {
        offer: [],
        public_rating: [],
        segment: [],
        network: [],
        subsidiary: [],
        opportunity_type: [],
        user_std_code: []
      },
      { keepDefaultValues: true }
    )
  }

  const fieldNames = Object.keys(currentSchema.fields) as TFieldNames[]

  const filtersList = useMemo(() => filters?.data?.data, [filters])

  const [subsidiariesList, setSubsidiariesList] = useState<string[]>()

  const subsidiaries = useMemo(() => subsidiariesList, [subsidiariesList])

  const networksList = filtersList?.networks.map((item) => item.network)

  const [usersList, setUsersList] = useState<TUsers[]>()

  const users = useMemo(() => usersList, [usersList])

  const PAGE_SIZE = 20

  const [userPage, setUserPage] = useState(1)

  const usersPaginated = useMemo(
    () => (users ? users.slice(0, userPage * PAGE_SIZE) : []),
    [users, userPage]
  )

  const hasMoreUsers = useMemo(
    () => (users?.length || 0) > usersPaginated.length,
    [users?.length, usersPaginated.length]
  )

  const loadMoreUsers = () => {
    setUserPage((p) => p + 1)
  }

  const formFields = watch()

  const allSubsidiaries = filtersList?.networks.reduce(
    //@ts-ignore
    (acc, actual) => acc.concat(actual['subsidiaries'] || []),
    []
  )

  useEffect(() => {
    if (filtersList?.users?.length) {
      setUsersList(filtersList?.users)

      // persist mapping for display in listings
      setUsersDisplayMap(filtersList.users).catch(() => {})
    }
  }, [filtersList?.users])

  useEffect(() => {
    const selectedNetworks = formFields.network ?? []

    if (selectedNetworks.length) {
      const subs = (filtersList?.networks || [])
        .filter((item) => selectedNetworks.includes(item.network))
        .reduce<string[]>((acc, n) => acc.concat(n.subsidiaries || []), [])

      setSubsidiariesList(subs)
    } else {
      setSubsidiariesList(allSubsidiaries)
    }

    // filter users by selected networks and subsidiaries (combined with next effect)
    const selectedSubsidiaries = formFields.subsidiary ?? []

    const baseUsers = filtersList?.users ?? []

    const filteredUsers = baseUsers.filter((u) => {
      const matchNetwork = selectedNetworks.length
        ? selectedNetworks.includes(u.network_name || '')
        : true

      const matchSubs = selectedSubsidiaries.length
        ? selectedSubsidiaries.includes(u.subsidiary_name || '')
        : true

      return matchNetwork && matchSubs
    })

    setUsersList(filteredUsers)

    setUserPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFields.network, formFields.subsidiary, filtersList?.networks, filtersList?.users])

  const fieldConfig: TFieldConfig = {
    offer: {
      options: filtersList?.offers ?? [],
      label: 'Por tipo de Oferta'
    },
    public_rating: {
      options: filtersList?.public_ratings ?? [],
      label: 'Por Público'
    },
    segment: {
      options: filtersList?.segments ?? [],
      label: 'Por Segmento'
    },
    network: {
      options: networksList ?? [],
      label: 'Por Rede'
    },
    subsidiary: {
      options: subsidiaries ?? [],
      label: 'Por Filial'
    },
    opportunity_type: {
      options: OPPORTUNITY_TYPES_OPTIONS,
      label: 'Por Origem'
    },
    user_std_code: {
      options: usersPaginated ?? [],
      label: 'Por Especialista'
    }
  }

  return {
    closeRightSheet,
    isMobile,
    control,
    handleSubmit,
    handleSubmitForm,
    handleResetForm,
    fieldNames,
    fieldConfig,
    isDirty: formState.isDirty,
    isLoading: isLoadingFilter || isRefetchingFilter,
    hasMoreUsers,
    loadMoreUsers
  }
}
